import { vendorApi } from '../../lib/vendorApi.ts';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface VendorMe {
  id: string;
  name: string;
  idCardNumber: string;
  email: string | null;
  phone: string | null;
}

export type QuotationStatus = 'submitted' | 'won' | 'lost' | 'pending';

export interface QuotationLine {
  name: string;
  unit: string;
  qty: number;
  unitPrice: number | null;
}

export interface VendorQuotation {
  requestId: string;
  refNumber: string;
  title: string;
  submittedAt: string;
  deadline: string | null;
  status: QuotationStatus;
  lines: QuotationLine[];
}

export interface OpenRequestItem {
  id: string;
  name: string;
  qty: number;
  unit: string;
  spec: string | null;
}

export interface OpenRequest {
  requestId: string;
  refNumber: string;
  title: string;
  deadline: string | null;
  specDoc: { name: string; size: number } | null;
  items: OpenRequestItem[];
}

export interface VendorAward {
  requestId: string;
  refNumber: string;
  title: string;
  awardedAt: string;
  amount: number;
  paid: boolean;
}

// ── Request detail (Task 1 shape) ──────────────────────────────────────────────

export interface RequestDetailLine {
  name: string;
  unit: string;
  qty: number;
  unitPrice: number | null;
  lineTotal: number;
}

export interface RequestDetailAwardInvoice {
  confirmed: boolean;
  confirmedAt: string | null;
  confirmedBankName: string | null;
  confirmedBankNumber: string | null;
}

export interface RequestDetailAwardPayment {
  paid: boolean;
  paidAt: string | null;
  hasSlip: boolean;
}

export interface RequestDetailAward {
  awardedAt: string;
  sentToFinanceAt: string | null;
  goodsReceivedAt: string | null;
  invoice: RequestDetailAwardInvoice;
  payment: RequestDetailAwardPayment;
}

export interface RequestDetail {
  request: {
    requestId: string;
    refNumber: string;
    title: string;
    status: string;
    deadline: string | null;
  };
  quotation: {
    submittedAt: string;
    lines: RequestDetailLine[];
    grandTotal: number;
  };
  award: RequestDetailAward | null;
}

export interface SubmitLineInput {
  requestItemId: string;
  unitPrice: number | null;
}

export interface SubmitPayload {
  confirmedDeclaration: boolean;
  lines: SubmitLineInput[];
}

export type VendorSubmitErrorCode =
  | 'deadline_passed'
  | 'already_submitted'
  | 'request_closed'
  | 'declaration_required'
  | 'invalid_lines'
  | 'no_prices'
  | 'invalid_price'
  | 'not_found';

// ── Client ────────────────────────────────────────────────────────────────────

// Fetch a vendor-authenticated PDF as a blob (Bearer token, not a cookie —
// mirrors documentsApi's downloadPdf but through the vendor axios instance).
async function fetchPdfBlob(url: string): Promise<Blob> {
  const response = await vendorApi.get<Blob>(url, { responseType: 'blob' });
  return new Blob([response.data], { type: 'application/pdf' });
}

export async function viewVendorPdf(url: string): Promise<void> {
  const blob = await fetchPdfBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
}

export async function downloadVendorPdf(url: string, filename: string): Promise<void> {
  const blob = await fetchPdfBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
}

// Download a vendor-authenticated file preserving its content type (spec docs
// may be images, not just PDFs).
export async function downloadVendorFile(url: string, filename: string): Promise<void> {
  const response = await vendorApi.get<Blob>(url, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
}

export const vendorPortalApi = {
  me: () => vendorApi.get<{ vendor: VendorMe }>('/me').then((r) => r.data.vendor),

  quotations: () =>
    vendorApi.get<{ quotations: VendorQuotation[] }>('/quotations').then((r) => r.data.quotations),

  openRequests: () =>
    vendorApi.get<{ requests: OpenRequest[] }>('/open-requests').then((r) => r.data.requests),

  awards: () => vendorApi.get<{ awards: VendorAward[] }>('/awards').then((r) => r.data.awards),

  submit: (requestId: string, payload: SubmitPayload) =>
    vendorApi.post<{ ok: true }>(`/requests/${requestId}/submit`, payload).then((r) => r.data),

  requestDetail: (requestId: string) =>
    vendorApi.get<RequestDetail>(`/requests/${requestId}`).then((r) => r.data),

  quotationPdfUrl: (requestId: string) => `/quotations/${requestId}/pdf`,
  invoicePdfUrl: (requestId: string) => `/awards/${requestId}/pdf`,
  slipUrl: (requestId: string) => `/awards/${requestId}/slip`,
  specDocUrl: (requestId: string) => `/requests/${requestId}/spec-doc`,
};
